import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AdminActionsService } from './admin-actions.service';
import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JcdFactoryService } from '../../../global/services/jcd-factory.service';
import { environment } from '../../../../environments/environment';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-admin-action-edit',
    templateUrl: './admin-action-edit.component.html',
    styleUrls: ['./admin-action-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminActionEditComponent implements OnInit, OnDestroy {
  loading = false;
  crudLoading = false;
  minimise = false;
  submitted = false;
  title = 'Action';
  mode: 'new' | 'edit' = 'new';

  secondaryActionOpen = false;
  cachedObjOpen = false;
  noActions = true;
  jsonValid = true;
  isJsonValid: boolean | undefined = undefined;

  action_data: any = {};
  private originalData: any = {};
  private rowData: any = {};

  jcd_required = 0;
  action_check_type = '';
  version_num = 0;

  permissionList = [
    { name: 'User', value: 1 },
    { name: 'Editor', value: 2 },
    { name: 'Admin', value: 3 },
    { name: 'Developer', value: 4 },
    { name: 'Super Admin', value: 5 },
  ];
  permissionSelected: any = null;

  actionTypeList = [
    { name: 'grid', value: 'grid' },
    { name: 'list', value: 'list' },
    { name: 'chart', value: 'chart' },
    { name: 'object', value: 'object' },
    { name: 'static json', value: 'static' },
    { name: 'update', value: 'update' },
    { name: 'delete', value: 'delete' },
  ];
  actionTypeSelected: any = null;

  actionCheckList = [
    { name: 'admin', value: 'admin' },
    { name: 'trans', value: 'trans' },
  ];

  methods = [
    { name: 'Before Action', value: 'pre' },
    { name: 'After Action', value: 'post' },
  ];

  datasourceList: any[] = [];
  datasourceSelected: any = null;

  validationRulesList: any[] = [];
  selectedDroolsRuleObj: any = {};

  cachingObjList: any[] = [];
  selectedCachingObjects: any[] = [];

  jcdlist: any = {
    channel: { list: [], selected: null, disabled: false },
    task: { list: [], taskList: [], selected: null, disabled: true },
    activity: { list: [], selected: null, disabled: true },
  };
  roles: any = { selected: null, list: [], loading: false, disabled: true };

  exampleInList = [
    { name: 'sso_id:String:null' },
    { name: 'sso_id:String:null,tax_year:String:2016' },
    { name: 'sso_id:String:null,tax_year:String:null' },
    { name: 'sso_id:String:null,tax_year:String:2015,quarter:String:4' },
    { name: 'sso_id:String:null,tax_year:String:null,scenario:String:18,jcd_key:String:250,combination_keys:String:0,parent_me_string:String:0,screen_key:String:18' },
  ];

  exampleOutList = [
    { name: 'resultset:OracleTypes.CURSOR,retval:OracleTypes.VARCHAR,error_out:OracleTypes.VARCHAR' },
    { name: 'retval:OracleTypes.VARCHAR,error_out:OracleTypes.VARCHAR' },
    { name: 'resultset:OracleTypes.CURSOR,resultset_RD:OracleTypes.CURSOR,retval:OracleTypes.VARCHAR,error_out:OracleTypes.VARCHAR' },
  ];

  exampleProcList = [
    { name: 'begin PKG_GLOBALTAX_SETTINGS.[SOMENAME](?,?,?,?);end;' },
    { name: 'begin PKG_GLOBALTAX_SETTINGS.[SOMENAME](?,?,?,?,?,?,?);end;' },
    { name: 'begin PKG_GLOBALTAX_SETTINGS.[SOMENAME](?,);end;' },
  ];

  private readonly baseUrl = environment.base_url;
  private readonly adminApiUrl = environment.admin_api_url;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<AdminActionEditComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private adminActionsService: AdminActionsService,
    private appService: AppService,
    private snackbar: GtwSnackbarService,
    private jcdFactory: JcdFactoryService,
    private cdr: ChangeDetectorRef,
  ) {
    this.rowData = data?.rowData || {};
    this.mode = data?.mode || (this.rowData?.ACTION_KEY ? 'edit' : 'new');
  }

  private get clientKey(): number | string {
    return this.appService.USER_SETTINGS?.user?.client_key
      ?? this.appService.USER_SETTINGS?.user?.client?.client_key
      ?? sessionStorage.getItem('client_key')
      ?? '';
  }

  private get clientDatasource(): string {
    return this.appService.USER_SETTINGS?.user?.client?.client_datasource || '';
  }

  ngOnInit(): void {
    this.initActionData();
    this.initJcd();
    this.loadDatasourceList();
    this.loadCachingObjList();

    if (this.mode === 'edit') {
      this.loadActionDetail(this.version_num);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initActionData(): void {
    this.action_data = {
      action_key: this.rowData?.ACTION_KEY || '',
      action_name: this.rowData?.ACTION_NAME || '',
      action_desc: this.rowData?.ACTION_DESC || '',
      active: this.rowData?.ACTIVE ?? 1,
      action_fetch_size: 100,
      use_cache: 'N',
      system_core: 0,
      datasource_id: this.clientDatasource,
      action_user_type_key: 1,
      proc_params_in: '',
      proc_params_out: '',
      proc_call: '',
      static_values: '',
      action_type: 'grid',
      client_key: this.clientKey,
      override: 0,
      response_settings: '',
      action_check: '',
      jcd_ta_key: this.rowData?.JCD_TA_KEY || null,
      jcd_ta_role_key: null,
      drools_validation_id: 0,
      action_version_num: 0,
      max_version_num: 0,
      min_version_num: 0,
      secondary_actions: [],
      cacheObjKeys: [],
    };

    this.jcd_required = (this.rowData?.JCD_TA_KEY) ? 1 : 0;
    this.permissionSelected = this.mode === 'edit' ? this.permissionList[0] : null;
    this.actionTypeSelected = this.actionTypeList[0];
    this.action_check_type = '';

    this.originalData = safeJsonInput<any>(JSON.stringify(this.action_data), null, 'admin-action-edit.parse1');
  }

  private loadActionDetail(versionNum: number): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.adminActionsService
      .getActionDetail(this.baseUrl, this.rowData.ACTION_KEY, versionNum, this.rowData.CLIENT_KEY)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res?.callSuccess === '1' && res?.jsonObject?.[0]) {
            const obj = res.jsonObject[0];
            const lower: any = {};
            Object.keys(obj).forEach((k) => {
              lower[k.toLowerCase()] = obj[k];
            });
            this.action_data = { ...this.action_data, ...lower };

            this.actionTypeSelected = this.actionTypeList.find(t => t.value === this.action_data.action_type) || this.actionTypeList[0];
            this.permissionSelected = this.permissionList.find(p => p.value === this.action_data.action_user_type_key) || this.permissionList[0];
            this.action_check_type = this.action_data.action_check || '';
            this.jcd_required = this.action_data.jcd_ta_key ? 1 : 0;
            if (this.jcd_required === 1 && this.action_data.jcd_ta_key) {
              this.loadRoles(this.action_data.jcd_ta_key, this.action_data.jcd_ta_role_key);
            }
            this.version_num = Number(this.action_data.action_version_num) || 0;

            if (this.action_data.override === 1 && this.datasourceList.length > 0) {
              this.datasourceSelected = this.datasourceList.find(
                (ds: any) => (ds.datasource_name || ds.DATASOURCE_NAME) === this.action_data.datasource_id
              ) || null;
            }

            if (typeof this.action_data.secondary_actions === 'string') {
              try {
                this.action_data.secondary_actions = safeJsonInput<any>(this.action_data.secondary_actions, null, 'admin-action-edit.parse2');
              } catch {
                this.action_data.secondary_actions = [];
              }
            }
            this.noActions = !this.action_data.secondary_actions || this.action_data.secondary_actions.length === 0;

            if (res.validationRules?.length) {
              this.validationRulesList = res.validationRules;
              this.selectedDroolsRuleObj = this.validationRulesList.find((r: any) => r.selected_rule === true) || {};
            }

            if (res.actionCacheMap?.length) {
              this.selectedCachingObjects = [];
              res.actionCacheMap.forEach((cacheMapObj: any) => {
                const found = this.cachingObjList.find((c: any) => c.id === cacheMapObj.id);
                if (found) {
                  this.selectedCachingObjects.push({
                    ...found,
                    delay_refresh: cacheMapObj.delay_refresh,
                    enabled: cacheMapObj.enabled,
                    refresh_order: cacheMapObj.refresh_order,
                  });
                }
              });
            }

            this.updateJsonValidity();
            this.originalData = safeJsonInput<any>(JSON.stringify(this.action_data), null, 'admin-action-edit.parse3');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Unable to load action details', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private loadDatasourceList(): void {
    this.adminActionsService.getDatasourceList(this.baseUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1' && res?.jsonObject) {
            this.datasourceList = res.jsonObject.map((item: any) => {
              const lower: any = {};
              Object.keys(item).forEach(k => lower[k.toLowerCase()] = item[k]);
              return lower;
            });
            if (this.action_data.override === 1) {
              this.datasourceSelected = this.datasourceList.find(
                (ds: any) => ds.datasource_name === this.action_data.datasource_id
              ) || null;
            }
          }
          this.cdr.detectChanges();
        },
      });
  }

  private loadCachingObjList(): void {
    this.adminActionsService.getCachingObjList(this.baseUrl, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1' && res?.jsonObject) {
            this.cachingObjList = res.jsonObject.map((item: any) => ({
              id: item.ID || item.id,
              label: item.LABEL || item.label,
            }));
          }
          this.cdr.detectChanges();
        },
      });
  }

  private initJcd(): void {
    const applyJcdLists = () => {
      this.buildJcdLists(this.action_data.jcd_ta_key || null);
      this.cdr.detectChanges();
    };

    if (this.jcdFactory.JCDFactory.JCDList?.length > 0) {
      applyJcdLists();
    } else {
      this.jcdFactory.loadJCDObj().then(() => applyJcdLists());
    }
  }

  private buildJcdLists(jcdTaKey: any): void {
    const jcdList: any[] = this.jcdFactory.JCDFactory.JCDList || [];
    let selectedChannel: any = null;
    let selectedTask: any = null;
    let selectedActivity: any = null;

    if (jcdTaKey) {
      const match = jcdList.find((i: any) => i.JCD_TA_KEY == jcdTaKey);
      if (match) {
        selectedChannel = match.CHANNEL_KEY;
        selectedTask = match.TASK_KEY;
        selectedActivity = match.ACTIVITY_KEY;
      }
    }

    const channelMap = new Map<number, any>();
    jcdList.forEach((v: any) => {
      if (!channelMap.has(v.CHANNEL_KEY)) {
        channelMap.set(v.CHANNEL_KEY, { name: v.CHANNEL_NAME, value: v.CHANNEL_KEY });
      }
    });
    const channelList = Array.from(channelMap.values());
    const channelSelected = selectedChannel ? channelList.find(c => c.value === selectedChannel) || null : null;

    this.jcdlist = {
      channel: { list: channelList, selected: channelSelected, disabled: false },
      task: { list: [], taskList: [], selected: null, disabled: !channelSelected },
      activity: { list: [], selected: null, disabled: true },
    };

    if (channelSelected) {
      this.buildTaskList(channelSelected.value, selectedTask, selectedActivity);
    }
  }

  private buildTaskList(channelKey: any, selectedTaskKey: any, selectedActivityKey: any): void {
    const jcdList: any[] = this.jcdFactory.JCDFactory.JCDList || [];
    const taskMap = new Map<number, any>();
    jcdList.forEach((v: any) => {
      if (v.CHANNEL_KEY == channelKey && !taskMap.has(v.TASK_KEY)) {
        taskMap.set(v.TASK_KEY, {
          name: v.TASK_NAME, value: v.TASK_KEY,
          parent_task_key: v.PARENT_TASK_KEY, level: v.level, indent: v.indent,
        });
      }
    });
    const taskList = Array.from(taskMap.values());
    const taskListTree = this.jcdFactory.addIndent(safeJsonInput<any>(JSON.stringify(taskList), null, 'admin-action-edit.parse4'));

    this.jcdlist.task = {
      list: taskList, taskList: taskListTree,
      selected: selectedTaskKey || null, disabled: false,
    };

    if (selectedTaskKey) {
      this.buildActivityList(channelKey, selectedTaskKey, selectedActivityKey);
    } else {
      this.jcdlist.activity = { list: [], selected: null, disabled: true };
    }
  }

  private buildActivityList(channelKey: any, taskKey: any, selectedActivityKey: any): void {
    const jcdList: any[] = this.jcdFactory.JCDFactory.JCDList || [];
    const actMap = new Map<number, any>();
    jcdList.forEach((v: any) => {
      if (v.CHANNEL_KEY == channelKey && v.TASK_KEY == taskKey && !actMap.has(v.ACTIVITY_KEY)) {
        actMap.set(v.ACTIVITY_KEY, { name: v.ACTIVITY_NAME, value: v.ACTIVITY_KEY });
      }
    });
    const actList = Array.from(actMap.values());
    const actSelected = selectedActivityKey ? actList.find(a => a.value === selectedActivityKey) || null : null;

    this.jcdlist.activity = { list: actList, selected: actSelected, disabled: false };

    if (actSelected) {
      const match = jcdList.find((i: any) =>
        i.CHANNEL_KEY == channelKey && i.TASK_KEY == taskKey && i.ACTIVITY_KEY == actSelected.value
      );
      this.action_data.jcd_ta_key = match?.JCD_TA_KEY || null;
    }
  }

  onJcdSelectChange(num: number): void {
    if (num <= 3) {
      const chanVal = this.jcdlist.channel?.selected?.value || null;
      this.jcdlist.task = { list: [], taskList: [], selected: null, disabled: !chanVal };
      this.jcdlist.activity = { list: [], selected: null, disabled: true };
      this.action_data.jcd_ta_key = null;
      if (chanVal) {
        this.buildTaskList(chanVal, null, null);
      }
    } else if (num === 4) {
      const chanVal = this.jcdlist.channel?.selected?.value || null;
      const taskVal = this.jcdlist.task?.selected || null;
      this.jcdlist.activity = { list: [], selected: null, disabled: true };
      this.action_data.jcd_ta_key = null;
      if (chanVal && taskVal) {
        this.buildActivityList(chanVal, taskVal, null);
      }
    } else if (num === 5) {
      const chanVal = this.jcdlist.channel?.selected?.value || null;
      const taskVal = this.jcdlist.task?.selected || null;
      const actVal = this.jcdlist.activity?.selected?.value || null;
      this.action_data.jcd_ta_key = null;
      if (chanVal && taskVal && actVal) {
        const jcdList: any[] = this.jcdFactory.JCDFactory.JCDList || [];
        const match = jcdList.find((i: any) =>
          i.CHANNEL_KEY == chanVal && i.TASK_KEY == taskVal && i.ACTIVITY_KEY == actVal
        );
        this.action_data.jcd_ta_key = match?.JCD_TA_KEY || null;
      }
    }

    this.loadRoles(this.action_data.jcd_ta_key, null);
    this.cdr.detectChanges();
  }

  private loadRoles(jcdTaKey: any, roleKey: any): void {
    if (!jcdTaKey) {
      this.roles = { selected: null, list: [], loading: false, disabled: true };
      return;
    }
    this.roles.loading = true;
    this.cdr.detectChanges();

    this.adminActionsService.getRoles(this.baseUrl, jcdTaKey)
      .pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        this.roles.loading = false;
        this.roles.disabled = false;
        if (data?.callSuccess === '1' && data?.jsonObject) {
          this.roles.list = data.jsonObject.map((item: any) => {
            const lower: any = {};
            Object.keys(item).forEach(k => lower[k.toLowerCase()] = item[k]);
            return lower;
          });
          if (roleKey) {
            this.roles.selected = this.roles.list.find((r: any) => r.jcd_ta_role_key === roleKey) || null;
          }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.roles.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  compareByValue(a: any, b: any): boolean {
    return a && b && a.value === b.value;
  }

  // --- Version navigation ---

  updateVersion(verNum: number): void {
    this.version_num = verNum;
    this.action_data.action_version_num = verNum;
    this.loadActionDetail(verNum);
  }

  get canGoPrevious(): boolean {
    return this.mode === 'edit'
      && this.action_data.min_version_num != null
      && this.action_data.min_version_num <= this.action_data.action_version_num - 1;
  }

  get canGoNext(): boolean {
    return this.mode === 'edit'
      && this.action_data.max_version_num != null
      && this.action_data.max_version_num >= this.action_data.action_version_num + 1;
  }

  // --- Field change handlers ---

  onPermissionChange(): void {
    this.action_data.action_user_type_key = this.permissionSelected?.value ?? 1;
    if (this.action_data.action_user_type_key <= 2) {
      this.jcd_required = 1;
    }
  }

  onActionTypeChange(): void {
    this.action_data.action_type = this.actionTypeSelected?.value ?? 'grid';
  }

  onActionCheckChange(): void {
    this.action_data.action_check = this.action_check_type;
  }

  onDatasourceChange(): void {
    this.action_data.datasource_id = this.datasourceSelected?.datasource_name ?? this.clientDatasource;
  }

  onSystemCoreChange(): void {
    if (this.action_data.system_core === 1) {
      this.action_data.datasource_id = 'globalTaxDS';
      this.action_data.override = 0;
    } else {
      this.setDatasourceFromOverride();
    }
    this.cdr.detectChanges();
  }

  onOverrideChange(): void {
    this.setDatasourceFromOverride();
    this.cdr.detectChanges();
  }

  private setDatasourceFromOverride(): void {
    if (this.action_data.system_core === 1) {
      this.action_data.datasource_id = 'globalTaxDS';
      this.action_data.override = 0;
    } else if (this.action_data.override === 1) {
      this.action_data.datasource_id = this.datasourceSelected?.datasource_name ?? null;
      this.action_check_type = 'trans';
      this.action_data.action_check = 'trans';
    } else {
      this.action_data.datasource_id = this.clientDatasource;
      this.action_check_type = 'trans';
      this.action_data.action_check = 'trans';
    }
  }

  onValidationRuleChange(): void {
    this.action_data.drools_validation_id = this.selectedDroolsRuleObj?.validation_key ?? 0;
  }

  // --- JSON validation ---

  updateJsonValidity(): void {
    const val = this.action_data.response_settings;
    if (!val || !String(val).trim()) {
      this.isJsonValid = undefined;
      this.jsonValid = true;
      return;
    }
    try {
      safeJsonInput<any>(val, null, 'admin-action-edit.parse5');
      this.isJsonValid = true;
      this.jsonValid = true;
    } catch {
      this.isJsonValid = false;
      this.jsonValid = false;
    }
  }

  // --- Example helpers ---

  setInPut(value: string): void {
    if (value) this.action_data.proc_params_in = value;
  }

  setOutPut(value: string): void {
    if (value) this.action_data.proc_params_out = value;
  }

  setProc(value: string): void {
    if (value) this.action_data.proc_call = value;
  }

  // --- Secondary Actions CRUD ---

  addSecondaryAction(): void {
    if (!this.action_data.secondary_actions) {
      this.action_data.secondary_actions = [];
    }
    this.action_data.secondary_actions.push({
      action_key: '',
      enabled: 0,
      execution_order: 0,
      pre_or_post: 'post',
      threshold: 30,
    });
    this.noActions = false;
    this.cdr.detectChanges();
  }

  deleteSecondaryAction(index: number): void {
    this.action_data.secondary_actions.splice(index, 1);
    this.noActions = this.action_data.secondary_actions.length === 0;
    this.cdr.detectChanges();
  }

  // --- Save / Reset / Cancel ---

  save(): void {
    this.submitted = true;
    this.cdr.detectChanges();

    if (this.crudLoading) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.action_data.action_name?.trim()) {
      this.snackbar.openSnackBar('Action Name is required', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    if (!this.jsonValid) {
      this.action_data.response_settings = '';
    }

    if (this.jcd_required !== 1) {
      this.action_data.jcd_ta_key = null;
      this.action_data.jcd_ta_role_key = null;
    } else if (this.roles.selected) {
      this.action_data.jcd_ta_role_key = this.roles.selected.jcd_ta_role_key;
    }

    this.action_data.client_key = this.action_data.client_key ?? this.clientKey;
    if (this.action_data.override == null) {
      this.action_data.override = 0;
    }
    this.action_data.action_check = this.action_check_type;
    this.action_data.drools_validation_id = this.selectedDroolsRuleObj?.validation_key ?? 0;
    this.action_data.cacheObjKeys = this.selectedCachingObjects;

    const actionKey = this.action_data.action_key ? String(this.action_data.action_key) : '';

    const payload = { ...this.action_data };
    if (this.mode === 'new') {
      delete payload.action_key;
      delete payload.static_values;
      delete payload.action_version_num;
      delete payload.max_version_num;
      delete payload.min_version_num;
      if (Array.isArray(payload.secondary_actions) && payload.secondary_actions.length === 0) {
        delete payload.secondary_actions;
      }
    }
    const jsonString = JSON.stringify(payload);

    this.adminActionsService
      .saveAction(this.adminApiUrl, { action_key: actionKey, json_string: jsonString })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.crudLoading = false;
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar('Action has been saved', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Save failed', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error saving action', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  reset(): void {
    this.submitted = false;
    this.action_data = safeJsonInput<any>(JSON.stringify(this.originalData), null, 'admin-action-edit.parse6');
    this.jcd_required = 0;
    this.version_num = 0;
    this.validationRulesList = [];
    this.selectedDroolsRuleObj = {};
    this.selectedCachingObjects = [];
    this.noActions = true;
    this.isJsonValid = undefined;
    this.permissionSelected = this.mode === 'edit' ? this.permissionList[0] : null;
    this.actionTypeSelected = this.actionTypeList[0];
    this.action_check_type = '';
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.dialogRef?.close();
  }
}
